import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, Lock, CheckCircle, Loader2, KeyRound } from 'lucide-react'
import { ExclamationTriangleIcon, TrashIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import PasswordStrengthIndicator from '../ui/PasswordStrengthIndicator'
import { validatePassword } from '../../utils/passwordValidation'
import toast from '../../utils/toast'
import DeleteAccountModal from '../ui/DeleteAccountModal'
import { trackChangePassword, trackDeleteAccount } from '../../config/analyticsEvents'

const AccountTab = ({ userInfo, onDeleteAccount, isDeletingAccount }) => {
  const { updatePassword, user } = useAuth()
  
  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  
  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const validatePasswordForm = () => {
    if (!oldPassword) {
      toast.error('Please enter your current password')
      return false
    }

    if (!newPassword) {
      toast.error('Please enter a new password')
      return false
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return false
    }
    
    const passwordValidation = validatePassword(newPassword)
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.errors[0])
      return false
    }
    
    return true
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    
    if (!validatePasswordForm()) {
      return
    }

    setIsChangingPassword(true)

    try {
      const { error } = await updatePassword(newPassword)
      
      if (error) {
        toast.error(error.message || 'Failed to update password')
      } else {
        trackChangePassword()
        toast.success('Password updated successfully!')
        // Clear form and close
        setOldPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setShowPasswordForm(false)
      }
    } catch (error) {
      console.error('Password update error:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleCancelPasswordChange = () => {
    setShowPasswordForm(false)
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleDeleteAccountClick = () => {
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async (captchaToken) => {
    try {
      await onDeleteAccount(captchaToken)
      trackDeleteAccount()
      setShowDeleteModal(false)
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Delete account error:', error)
      throw error // Re-throw to let the modal handle captcha reset
    }
  }

  const passwordsMatch = confirmPassword && newPassword === confirmPassword
  const passwordsDontMatch = confirmPassword && newPassword !== confirmPassword

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h2>
        <p className="text-gray-600">Manage your account security and preferences</p>
      </div>

      {/* Sign in methods Section */}
      <motion.div 
        className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Sign in methods</h3>
          <p className="text-sm text-gray-500 mt-1">Manage how you sign in to your account</p>
        </div>
        <div className="p-6">
          {/* Password Item */}
          <div className="border border-gray-200 rounded-lg">
            <div 
              className={`flex items-center justify-between p-4 ${showPasswordForm ? 'border-b border-gray-200' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <KeyRound className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Password</h4>
                  <p className="text-sm text-gray-500">Use a password to sign in to your account</p>
                </div>
              </div>
              {!showPasswordForm && (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Change password
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Expandable Password Form */}
            <AnimatePresence>
              {showPasswordForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-gray-50">
                    <form onSubmit={handleChangePassword} className="space-y-5 max-w-md">
                      {/* Old Password */}
                      <div className="space-y-2">
                        <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700">
                          Current Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                          <input
                            id="oldPassword"
                            type={showOldPassword ? 'text' : 'password'}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors bg-white"
                            placeholder="Enter your current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showOldPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      {/* New Password */}
                      <div className="space-y-2">
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                          New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                          <input
                            id="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors bg-white"
                            placeholder="Enter your new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        {newPassword && <PasswordStrengthIndicator password={newPassword} className="mt-4" />}
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-2">
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                          <input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={`pl-10 pr-10 w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500/20 transition-colors bg-white ${
                              passwordsDontMatch 
                                ? 'border-red-300 focus:border-red-500' 
                                : passwordsMatch
                                ? 'border-green-300 focus:border-green-500'
                                : 'border-gray-300 focus:border-blue-500'
                            }`}
                            placeholder="Confirm your new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        {passwordsDontMatch && (
                          <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                        )}
                        {passwordsMatch && (
                          <p className="text-green-600 text-sm mt-1 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Passwords match
                          </p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex gap-3">
                          <button
                            type="submit"
                            disabled={isChangingPassword || !oldPassword || !newPassword || !confirmPassword || passwordsDontMatch}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                          >
                            {isChangingPassword ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              'Update Password'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelPasswordChange}
                            disabled={isChangingPassword}
                            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                          >
                            Cancel
                          </button>
                        </div>
                        <Link
                          to="/forgot-password"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          I forgot my password
                        </Link>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Danger Zone - Delete Account */}
      <motion.div 
        className="bg-white rounded-xl shadow-sm border border-red-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900">Danger Zone</h3>
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h4 className="text-base font-medium text-gray-900">Delete Account</h4>
              <p className="text-sm text-gray-500 mt-1">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
            </div>
            <button
              onClick={handleDeleteAccountClick}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 border border-red-300 rounded-lg transition-colors duration-200 whitespace-nowrap"
            >
              <TrashIcon className="h-4 w-4" />
              Delete Account
            </button>
          </div>
        </div>
      </motion.div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingAccount}
        userEmail={user?.email}
      />
    </div>
  )
}

export default AccountTab
